import React, { useMemo, useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import { Button, useNotify, useRefresh } from "react-admin";
import dataProvider from "server/dataProvider";
import { dormitoryRequests } from "api/dormitoryApi";
import ConfirmModal from "server/components/Modals/ConfirmModal";
import { useNotifyError } from "utils/useNotifyError";

// Moving in happens on a calendar day in the dormitory's own timezone, so every date here is read and
// written in local parts. toISOString() would render the evening of one local day as the next UTC day
// and hand the backend an arrival it rejects as being in the future.
const toLocalDateInput = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const todayLocal = () => toLocalDateInput(new Date());

// Backend timestamps arrive as ISO strings; only the calendar day they fall on bounds the picker.
const toDateBound = value => {
  if (!value) return null;

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime()) ? null : toLocalDateInput(parsed);
};

// yyyy-mm-dd strings compare correctly as plain strings, so the bounds need no date arithmetic.
const lowest = values => values.filter(Boolean).sort()[0] || null;
const highest = values => values.filter(Boolean).sort().pop() || null;

// The window the backend will accept: it opens when the accommodation period does and closes at
// whichever comes first of today, the end of that period and the appearance deadline. An arrival is
// never in the future, but an ordinary on-time backdated entry inside the window stays allowed.
const checkInBounds = application => {
  const today = todayLocal();
  const min = toDateBound(application?.accommodationFrom);
  const max = lowest([today, toDateBound(application?.accommodationTo), toDateBound(application?.appearanceDeadline)]);

  return { min, max };
};

const clampToBounds = (value, { min, max }) => highest([lowest([value, max]), min]);

const CheckInModal = ({ record }) => {
  const notify = useNotify();
  const notifyError = useNotifyError();
  const refresh = useRefresh();
  // The row that rendered the button may predate a cancellation, so the application is read again when
  // the dialog opens and everything below - eligibility and date bounds alike - comes from that read.
  const [application, setApplication] = useState(null);
  const [movedInAt, setMovedInAt] = useState(todayLocal());

  const campaignId = record?.campaignId;
  const applicationId = record?.applicationId || record?.id;

  const bounds = useMemo(() => checkInBounds(application), [application]);

  const dateError = useMemo(() => {
    if (!movedInAt) return "Моля, изберете дата на настаняване.";
    if (movedInAt > todayLocal()) return "Датата на настаняване не може да бъде в бъдещето.";
    if (bounds.min && movedInAt < bounds.min) return "Датата е преди началото на периода за настаняване.";
    if (bounds.max && movedInAt > bounds.max) return "Датата е след края на периода или срока за явяване.";

    return null;
  }, [bounds, movedInAt]);

  // Reads the application back and reports whether it may still be checked in. Used both before the
  // dialog opens and after a backend rejection - a read only, it changes nothing on the server.
  const loadEligibility = async () => {
    const { data } = await dataProvider.customRequest(
      dormitoryRequests.getCampaignApplicationById(campaignId, applicationId),
      null,
      "GET",
    );
    setApplication(data);

    return data;
  };

  const handleOpen = async () => {
    if (!campaignId || !applicationId) {
      notify("Липсва идентификатор на молбата.", { type: "error" });
      return false;
    }

    try {
      const fresh = await loadEligibility();
      if (fresh?.canCheckIn !== true) {
        notify("Молбата вече не подлежи на настаняване.", { type: "warning" });
        refresh();
        return false;
      }

      // Today unless the accepted window has already closed behind it, in which case the last day
      // of that window is the sensible starting point.
      setMovedInAt(clampToBounds(todayLocal(), checkInBounds(fresh)) || todayLocal());
    } catch (err) {
      notifyError(err);
      return false;
    }

    return true;
  };

  const handleCheckIn = async () => {
    if (dateError) {
      notify(dateError, { type: "warning" });
      return false;
    }

    try {
      await dataProvider.customRequest(
        dormitoryRequests.createAccommodation(),
        {
          applicationId,
          movedInAt: `${movedInAt}T00:00:00`,
        },
        "POST",
      );
      notify("Студентът е настанен успешно и е преместен в меню 'Настанени студенти'!", { type: "success" });
      refresh();
      return true;
    } catch (err) {
      // One notification for the rejection, then the eligibility is re-read so the dialog reflects
      // whatever changed underneath it. The typed date is kept so the correction can be made in place.
      notifyError(err);
      try {
        await loadEligibility();
      } catch {
        // The rejection is already on screen; a failed re-read must not replace it with a second one.
      }

      return false;
    }
  };

  const fullName = record?.studentFullName || "—";

  return (
    <ConfirmModal
      onOpen={handleOpen}
      onConfirm={handleCheckIn}
      titleContent="Настаняване в общежитие"
      saveTitle="Потвърди настаняване"
      closeTitle="Отказ"
      confirmEnabled={!dateError}
      resetProps={() => {
        setApplication(null);
        setMovedInAt(todayLocal());
      }}
      dialogProps={{
        sx: {
          "& .MuiDialogTitle-root": { display: "none" },
          "& .MuiDialogContent-root": { fontFamily: "inherit", paddingTop: 3 },
          "& .MuiInputBase-root, & .MuiInputLabel-root, & .MuiFormHelperText-root, & .MuiDialogActions-root": {
            fontFamily: "inherit",
          },
        },
      }}
      triggerButton={
        <Button
          startIcon={<MeetingRoomIcon />}
          label="Настани"
        />
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <Typography variant="body2">
          Студент: <strong>{fullName}</strong>
        </Typography>
        {record?.assignedRoomNumber && (
          <Typography variant="body2" color="text.secondary">
            Разпределение: Блок <strong>{record?.assignedResidentialNumber || "—"}</strong>, Стая <strong>{record?.assignedRoomNumber}</strong>
          </Typography>
        )}

        <TextField
          type="date"
          variant="filled"
          size="small"
          label="Дата на настаняване"
          value={movedInAt}
          onChange={e => setMovedInAt(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ min: bounds.min || undefined, max: bounds.max || undefined }}
          fullWidth
          required
          error={!!dateError}
          helperText={dateError || "По подразбиране е избрана днешна дата. Бъдеща дата не се приема."}
        />
      </Box>
    </ConfirmModal>
  );
};

export default CheckInModal;
